/**
 * Section - Full-width section with optional PageContentWrapper
 *
 * Use for page sections that need full-width backgrounds
 * but centered content.
 *
 * Usage:
 * <Section bg="white">...</Section>                    // Full-width bg, centered content
 * <Section noPadding>...</Section>                     // Full-width, no wrapper
 * <Section maxWidth="narrow" bg="gray.100">...</Section>  // Narrower content
 */

import { ReactNode } from "react";
import Box, { BoxProps } from "@ui-kit/layout/Box/Box.web";
import { MaxWidthKey } from "@shared/config/layout.config";
import { PageContentWrapper } from "../PageContentWrapper/PageContentWrapper.web";

export interface SectionProps extends BoxProps {
  children?: ReactNode;
  /**
   * Max width for the content wrapper
   * @default "content"
   */
  maxWidth?: MaxWidthKey | string;
  /**
   * Disable horizontal padding
   * @default false
   */
  noPadding?: boolean;
  /**
   * Disable padding on mobile (base breakpoint), apply padding from sm up
   * @default false
   */
  noPaddingMobile?: boolean;
}

export function Section({
  maxWidth = "content",
  noPadding = false,
  noPaddingMobile = false,
  children,
  ...props
}: SectionProps): JSX.Element {
  return (
    <Box width="100%" {...props}>
      <PageContentWrapper
        maxWidth={maxWidth}
        noPadding={noPadding}
        noPaddingMobile={noPaddingMobile}
      >
        {children}
      </PageContentWrapper>
    </Box>
  );
}

export default Section;
